import { readStat, LOWER_IS_BETTER_STAT_PATHS } from './metrics';
import type { PlayerStatsRow } from '@api-service/player-stats';

const PERCENTILE_SCALE = 100;

/**
 * Percentile of `value` within `population`, as the share of the population it
 * beats. The compared player's own row must not be in `population` (the hook
 * excludes it): a player cannot strictly beat himself, so leaving it in caps
 * the league leader at 98th instead of 100th. For metrics where a lower number
 * is better — goals conceded, big chances conceded — the scale is inverted so
 * a high percentile always reads as a good result.
 */
export const toPercentile = (
  value: number,
  population: number[],
  isLowerBetter: boolean
): number | null => {
  if (population.length === 0) return null;

  const beaten = population.filter((other) =>
    isLowerBetter ? other > value : other < value
  ).length;

  return (beaten / population.length) * PERCENTILE_SCALE;
};

/**
 * Collects one comparison population per stat path from a page of stats rows,
 * skipping rows where the API returned nothing for that path so an absent value
 * is not counted as a zero.
 */
export const buildPopulations = (
  rows: PlayerStatsRow[],
  statPaths: string[]
): Map<string, number[]> => {
  const populations = new Map<string, number[]>(
    statPaths.map((path) => [path, []])
  );

  for (const row of rows) {
    for (const path of statPaths) {
      const value = readStat(row, path);
      if (value !== null) populations.get(path)?.push(value);
    }
  }

  return populations;
};

export type PercentileLookup = (
  statPath: string,
  value: number | null
) => number | null;

export const createPercentileLookup = (
  populations: Map<string, number[]>
): PercentileLookup => {
  return (statPath, value) => {
    if (value === null) return null;
    const population = populations.get(statPath);
    if (!population || population.length === 0) return null;
    return toPercentile(
      value,
      population,
      LOWER_IS_BETTER_STAT_PATHS.has(statPath)
    );
  };
};

const STRONG_PERCENTILE = 70;
const MIDDLING_PERCENTILE = 40;

/** Bar fill colour for a percentile: strong, middling, or weak. */
export const percentileFillClass = (percentile: number | null): string => {
  if (percentile === null) return 'bg-neutral-300';
  if (percentile >= STRONG_PERCENTILE) return 'bg-success-500';
  if (percentile >= MIDDLING_PERCENTILE) return 'bg-primary-600';
  return 'bg-neutral-400';
};
